import once from 'lodash-es/once';
import React, {
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useFetchLoginContext } from 'src/hooks/useFetchLoginContext';
import { ContentId } from 'src/utils/constants/contentId';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import { lookupEventInCatalog } from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  CatalogClient,
  Event,
  Listing,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  PosClientConfig,
  PurchaseClient,
  PurchaseOrderDetails,
  Sale,
  SaleClient,
  SaleDetails,
} from 'src/WebApiController';
import {
  createContext as createContextWithSelector,
  useContext as useContextWithSelector,
} from 'use-context-selector';

import { useAppContext } from '../AppContext';
import {
  useCatalogDataContextDataSelector,
  useCatalogDataContextUpdateItemInEventSelector,
} from '../CatalogDataContext';
import { Content } from '../ContentContext';
import {
  useSearchDataContextIsEnabledSelector,
  useSearchDataContextUpdateItemInEventSelector,
} from '../SearchDataContext';
import {
  getPosEntityStopwatchLoggingId,
  getStopwatchGapsForActivePosEntityPageLoad,
  StopwatchLoggingEventName,
  useStopwatchLoggingContext,
} from '../StopwatchLoggingContext';
import { IActivePosEntityContext, IPosEntity } from './ActivePosEntityContext';

// Type for entity getter return values
export type EntityGetterResult<TEntity extends IPosEntity = IPosEntity> = {
  posEntityId?: number;
  posEntity?: TEntity | null;
  event?: Event | null;
  posEntityDisplayId?: string | null;
};

// Type for partial entity state during loading
type PartialEntityState = {
  id: number;
  displayId: string | null;
  type: ActionOutboxEntityType;
  data: IPosEntity | null;
  event: Event | null;
  loadedAt?: number;
  isLoading: boolean;
  error?: any;
};

// Default entity getter functions
const defaultGetSaleEntity = async (
  posEntityId: number,
  _?: Sale | null, // Current Sale entity if already loaded
  __?: string[] | null,
  activeAccountWebClientConfig?: PosClientConfig
): Promise<EntityGetterResult<Sale>> => {
  if (!activeAccountWebClientConfig?.activeAccountId) {
    throw new Error(
      'activeAccountWebClientConfig is required for getSaleEntity'
    );
  }

  const saleDetail = await new SaleClient(
    activeAccountWebClientConfig
  ).getSaleBySaleId(posEntityId);

  if (saleDetail) {
    return {
      posEntityId: saleDetail.id,
      posEntity: saleDetail,
      event: null,
      posEntityDisplayId: saleDetail.idOnMkp,
    };
  }

  return {};
};

const defaultGetListingEntity = async (
  posEntityId: number,
  curEntity?: ListingDetails | null, // Current ListingDetails if already loaded
  dataToGet?: string[] | null, // Cast to ListingDetailDataField[] when used
  activeAccountWebClientConfig?: PosClientConfig
): Promise<EntityGetterResult<ListingDetails>> => {
  if (!activeAccountWebClientConfig?.activeAccountId) {
    throw new Error(
      'activeAccountWebClientConfig is required for getListingEntity'
    );
  }

  const listingDetails = await new ListingClient(
    activeAccountWebClientConfig
  ).getListingByListingId(
    posEntityId,
    curEntity ?? undefined,
    dataToGet?.map((s) => s as ListingDetailDataField) ?? []
  );
  if (listingDetails) {
    return {
      posEntityId: listingDetails.id,
      posEntity: listingDetails,
      event: null,
      posEntityDisplayId: listingDetails.idOnMkp,
    };
  }
  return {};
};

const defaultGetPurchaseEntity = async (
  posEntityId: number,
  _?: PurchaseOrderDetails | null, // Current PurchaseOrderDetails if already loaded
  __?: string[] | null,
  activeAccountWebClientConfig?: PosClientConfig
): Promise<EntityGetterResult<PurchaseOrderDetails>> => {
  if (!activeAccountWebClientConfig?.activeAccountId) {
    throw new Error(
      'activeAccountWebClientConfig is required for getPurchaseEntity'
    );
  }

  const purchaseDetails = await new PurchaseClient(
    activeAccountWebClientConfig
  ).getPurchaseOrderByPurchaseOrderId(posEntityId, false);

  if (purchaseDetails) {
    return {
      posEntityId: purchaseDetails.id,
      posEntity: purchaseDetails,
      event: null,
      posEntityDisplayId: purchaseDetails.id.toString(),
    };
  }
  return {};
};

// Generic type for entity getter functions
export type EntityGetterType<TEntity extends IPosEntity> = (
  posEntityId: number,
  curEntity?: TEntity | null,
  dataToGet?: string[] | null,
  activeAccountWebClientConfig?: PosClientConfig
) => Promise<EntityGetterResult<TEntity>>;

// Specific entity getter types
export type GetSaleEntityType = EntityGetterType<SaleDetails>;
export type GetListingEntityType = EntityGetterType<ListingDetails>;
export type GetPurchaseEntityType = EntityGetterType<PurchaseOrderDetails>;

// Type definition for setActivePosEntity function signature
export type SetActivePosEntityType = (
  posEntityId: number,
  posEntityDisplayId?: string | null,
  forceRefresh?: boolean,
  dataToGet?: string[],
  resetData?: boolean,
  sectional?: boolean,
  posEntityType?: ActionOutboxEntityType
) => Promise<void | null>;

// Extended interface for V2 with multi-entity capabilities
export interface IMultiEntityContext<TDataItem extends IPosEntity> {
  entityType: ActionOutboxEntityType;
  posEntityId?: number | null;
  /* Null means a value was not able to be obtained.  Undefined means the item was never retrieved or being retrieved. */
  posEntity?: TDataItem | null;
  /* Null means a value was not able to be obtained.  Undefined means the item was never retrieved or being retrieved. */
  event?: Event | null;
  /* Whether the current active event item is being searched or just loading */
  posEntityDisplayId?: string | null;
  // Override setActivePosEntity to add the new posEntityType parameter
  setActivePosEntity: SetActivePosEntityType;
  updateActivePosEntityInfo: (
    posEntity: Pick<
      IMultiEntityContext<TDataItem>,
      'posEntityId' | 'event' | 'posEntity' | 'posEntityDisplayId'
    >
  ) => void;
  isLoading: boolean;
  errorInfo?: { errorHeader: React.ReactNode; errorMessage: React.ReactNode };
  // Direct entity access methods - no race conditions
  getSaleEntity: () => SaleDetails | null;
  getListingEntity: () => ListingDetails | null;
  getPurchaseEntity: () => PurchaseOrderDetails | null;
}

export const createActivePosEntityContext = once(<
  TDataItem extends IPosEntity,
>() => React.createContext({} as IMultiEntityContext<TDataItem>));

export const createActivePosEntityContextWithSelector = once(<
  TDataItem extends IPosEntity,
>() => createContextWithSelector({} as IMultiEntityContext<TDataItem>));

/**
 * @deprecated use the explicit context selectors to avoid unnecessary re-render
 */
export const useActivePosEntityContext = <TDataItem extends IPosEntity>() => {
  const contextWithSelector = useContextWithSelector(
    createActivePosEntityContextWithSelector<TDataItem>()
  );
  return contextWithSelector;
};

const ActivePosEntityContextV2ProviderRaw = <TDataItem extends IPosEntity>({
  entityType,
  getActivePosEntity,
  getSaleEntity,
  getListingEntity,
  getPurchaseEntity,
  onSetActiveItemCompleteCallback,
  doNotLogPageLoad,
  children,
}: {
  entityType: ActionOutboxEntityType;
  getActivePosEntity?: (
    posEntityId: number,
    curEntity?: TDataItem | null,
    dataToGet?: string[] | null
  ) => Promise<
    Pick<
      IActivePosEntityContext<TDataItem>,
      'posEntityId' | 'event' | 'posEntity' | 'posEntityDisplayId'
    >
  >;
  getSaleEntity?: GetSaleEntityType;
  getListingEntity?: GetListingEntityType;
  getPurchaseEntity?: GetPurchaseEntityType;
  onSetActiveItemCompleteCallback?: (
    item: TDataItem,
    forceRefresh?: boolean
  ) => void;
  doNotLogPageLoad?: boolean;
  children: React.ReactNode;
}) => {
  const { trackError } = useClientLogContext();
  const [errorInfo, setErrorInfo] = useState<{
    errorHeader: React.ReactNode;
    errorMessage: React.ReactNode;
  }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [entityStates, setEntityStates] = useState<
    Record<string, PartialEntityState>
  >({});
  const [activeActionOutboxEntityType, setActiveActionOutboxEntityType] =
    useState<ActionOutboxEntityType | null>(null);

  const data = useCatalogDataContextDataSelector();
  const updateItemInEvent = useCatalogDataContextUpdateItemInEventSelector();

  const searchDataEnabled = useSearchDataContextIsEnabledSelector();
  const searchDataUpdateItemInEvent =
    useSearchDataContextUpdateItemInEventSelector();
  const { activeAccountWebClientConfig, setLoginContext } = useAppContext();
  const { startEvent, addStopGap } = useStopwatchLoggingContext();
  const MultiEntityContext = createActivePosEntityContext();

  const { fetchLoginContext } = useFetchLoginContext();
  // Race condition can happen where login context is not initialized
  // i.e. when deeplink to open the entity modal
  // In such scenario, we need to force refetch and update the login context
  const sanitizeLoginContext = useCallback(async (): Promise<
    PosClientConfig | undefined
  > => {
    if (activeAccountWebClientConfig?.activeAccountId) {
      return activeAccountWebClientConfig;
    }

    const loginContext = await fetchLoginContext(setLoginContext, (error) => {
      trackError('LoginClient.getLoginContext', error, {
        trackErrorData: {
          returnUrl: window.location.pathname,
          loginContext,
        },
      });
    });
    if (!loginContext) {
      return undefined;
    }
    return { activeAccountId: loginContext.user?.activeAccount?.id };
  }, [
    activeAccountWebClientConfig,
    fetchLoginContext,
    setLoginContext,
    trackError,
  ]);

  // Create entity getter function that binds fresh config at call time
  const handleEntityLoad = useCallback(
    async (
      targetEntityType: ActionOutboxEntityType,
      entityId: number,
      curEntity?: Sale | ListingDetails | PurchaseOrderDetails | null,
      dataToGet?: string[],
      clientConfig?: PosClientConfig
    ) => {
      switch (targetEntityType) {
        case ActionOutboxEntityType.Sale: {
          // Use provided getter or fall back to default
          const saleGetter = getSaleEntity || defaultGetSaleEntity;
          return saleGetter(
            entityId,
            curEntity as SaleDetails | null,
            dataToGet,
            clientConfig
          );
        }
        case ActionOutboxEntityType.Listing: {
          // Use provided getter or fall back to default
          const listingGetter = getListingEntity || defaultGetListingEntity;
          return listingGetter(
            entityId,
            curEntity as ListingDetails | null,
            dataToGet,
            clientConfig
          );
        }
        case ActionOutboxEntityType.Purchase: {
          // Use provided getter or fall back to default
          const purchaseGetter = getPurchaseEntity || defaultGetPurchaseEntity;
          return purchaseGetter(
            entityId,
            curEntity as PurchaseOrderDetails | null,
            dataToGet,
            clientConfig
          );
        }
        default: {
          // Fall back to primary getter for backwards compatibility
          if (getActivePosEntity && targetEntityType === entityType) {
            return getActivePosEntity(
              entityId,
              curEntity as TDataItem | null,
              dataToGet
            );
          }
          break;
        }
      }
      throw new Error(
        `No entity getter available for type: ${targetEntityType}`
      );
    },
    [
      getSaleEntity,
      getListingEntity,
      getPurchaseEntity,
      getActivePosEntity,
      entityType,
    ]
  );

  // Additional entity getters can be registered by other providers or components
  // This allows for multi-entity functionality across different routes

  // Get the currently active entity
  const activeEntityState = useMemo(
    () =>
      activeActionOutboxEntityType
        ? entityStates[activeActionOutboxEntityType]
        : null,
    [activeActionOutboxEntityType, entityStates]
  );

  const getAndUpdateCatalogDataForEntity = useCallback(
    async (
      item: IPosEntity,
      sectional = false,
      itemEntityType?: ActionOutboxEntityType,
      clientConfig?: PosClientConfig
    ) => {
      const targetEntityType = itemEntityType || entityType; // Fallback to provider type

      if (
        !item?.viagVirtId ||
        (targetEntityType !== ActionOutboxEntityType.Listing &&
          targetEntityType !== ActionOutboxEntityType.Sale)
      ) {
        return null;
      }

      let event = lookupEventInCatalog(data, item.viagVirtId, item.posEvId);

      if (event == null) {
        if (clientConfig) {
          const client = new CatalogClient(clientConfig);
          const results =
            targetEntityType === ActionOutboxEntityType.Listing
              ? await client.getCatalogForListingByListingId(item.id!)
              : await client.getCatalogForSaleBySaleId(item.id!);
          if (results?.events) {
            event = lookupEventInCatalog(
              results,
              item.viagVirtId,
              item.posEvId
            );
          }
        }
      } else {
        if (targetEntityType === ActionOutboxEntityType.Listing) {
          updateItemInEvent(
            item as unknown as Listing,
            targetEntityType,
            sectional
          );
        } else {
          updateItemInEvent(item as unknown as Sale, targetEntityType);
        }
      }

      // Also push to SearchDataContext localOverrides for V2 table updates
      if (searchDataEnabled) {
        // Derive internalNotesPreview from privNotes if not populated
        // (the detail API returns privNotes but not internalNotesPreview)
        const entity = item as unknown as Listing | Sale;
        if (entity.privNotes != null && entity.internalNotesPreview == null) {
          const maxLen = 80;
          entity.internalNotesPreview =
            entity.privNotes.length > maxLen
              ? entity.privNotes.substring(0, maxLen)
              : entity.privNotes;
          entity.isInternalNotesPreviewTruncated =
            entity.privNotes.length > maxLen;
        }

        if (targetEntityType === ActionOutboxEntityType.Listing) {
          searchDataUpdateItemInEvent(
            entity as Listing,
            targetEntityType,
            sectional
          );
        } else {
          searchDataUpdateItemInEvent(entity as Sale, targetEntityType);
        }
      }

      return event;
    },
    [
      data,
      entityType,
      searchDataEnabled,
      searchDataUpdateItemInEvent,
      updateItemInEvent,
    ]
  );

  const updateActivePosEntityInfo = useCallback(
    async (
      item: Pick<
        IMultiEntityContext<IPosEntity>,
        'posEntityId' | 'event' | 'posEntity' | 'posEntityDisplayId'
      >
    ) => {
      // Infer entity type using the same logic as setActivePosEntity
      const findEntityTypeForId = (entityId: number) => {
        for (const [type, entity] of Object.entries(entityStates)) {
          if (entity?.id === entityId) {
            return type as ActionOutboxEntityType;
          }
        }
        return null;
      };

      const targetEntityType = item.posEntityId
        ? findEntityTypeForId(item.posEntityId) || entityType // Infer or fallback to provider type
        : entityType;

      const sanitizedClientConfig = await sanitizeLoginContext();
      const event = item.posEntity
        ? await getAndUpdateCatalogDataForEntity(
            item.posEntity,
            false,
            targetEntityType, // Use inferred/fallback entity type
            sanitizedClientConfig
          )
        : undefined;

      // Update entity state with new info
      if (item.posEntityId) {
        const entityKey = targetEntityType; // Use inferred entity type
        setEntityStates((prev) => ({
          ...prev,
          [entityKey]: {
            ...prev[entityKey],
            data: item.posEntity as IPosEntity,
            event: event?.event || null,
            displayId: item.posEntityDisplayId || null,
          },
        }));
      }
    },
    [
      entityType,
      sanitizeLoginContext,
      getAndUpdateCatalogDataForEntity,
      entityStates,
    ] // Add entityStates to deps
  );

  // Use refs to avoid recreating callbacks when these values change
  const startEventRef = useRef(startEvent);
  startEventRef.current = startEvent;
  const addStopGapRef = useRef(addStopGap);
  addStopGapRef.current = addStopGap;
  const entityStatesRef = useRef(entityStates);
  entityStatesRef.current = entityStates;
  // Unified setActivePosEntity with cleaner signature using explicit posEntityType
  const setActivePosEntity = useCallback(
    async (
      posEntityId: number,
      posEntityDisplayId?: string | null | number,
      forceRefresh?: boolean,
      dataToGet?: string[],
      resetData?: boolean,
      sectional = false,
      posEntityType?: ActionOutboxEntityType
    ) => {
      if (isLoading) {
        return;
      }

      let actualEntityType: ActionOutboxEntityType;
      const actualEntityId: number = posEntityId;
      let actualForceRefresh = forceRefresh;

      // V2 detection: If posEntityType is provided, use V2 behavior
      if (posEntityType) {
        // V2 signature with explicit posEntityType parameter
        actualEntityType = posEntityType;
        actualForceRefresh = forceRefresh;
      } else {
        // V1 signature: Fall back to entity lookup or provider type

        // Find which entity type currently has this ID (for cross-entity scenarios)
        const findEntityTypeForId = (entityId: number) => {
          for (const [type, entity] of Object.entries(
            entityStatesRef.current
          )) {
            if (entity?.id === entityId) {
              return type as ActionOutboxEntityType;
            }
          }
          return null;
        };

        const foundEntityType = findEntityTypeForId(actualEntityId);
        actualEntityType = foundEntityType || entityType; // Fallback to provider type

        actualForceRefresh = forceRefresh || resetData;
      }

      if (actualEntityId === 0) {
        setActiveActionOutboxEntityType(null);
        return;
      }

      const currentEntity = entityStatesRef.current[actualEntityType];
      const isSameEntity = currentEntity?.id === actualEntityId;

      // Always set the active entity key when switching entity types
      setActiveActionOutboxEntityType(actualEntityType);

      if (!sectional && (forceRefresh || !isSameEntity)) {
        // If we are getting a different item, clear the existing ones so it shows a loading state
        setEntityStates((prev) => ({
          ...prev,
          [actualEntityType]: {
            id: actualEntityId,
            displayId:
              posEntityDisplayId != null ? String(posEntityDisplayId) : null,
            type: actualEntityType,
            data: null, // Clear detailed data
            event: null, // Clear event
            isLoading: true, // Loading state
          },
        }));
      }

      const logKey = `ActivePosEntity-${actualEntityType}-${actualEntityId}${dataToGet?.length ? '-' + dataToGet.join('-') : ''}`;

      const shouldLogData = !doNotLogPageLoad && Boolean(actualEntityId);

      return tryInvokeApi(
        async () => {
          setErrorInfo(undefined);
          setIsLoading(true);

          if (shouldLogData) {
            startEventRef.current(
              logKey,
              getPosEntityStopwatchLoggingId(actualEntityType),
              getStopwatchGapsForActivePosEntityPageLoad(actualEntityType),
              actualEntityType
            );
          }

          const existingEntity = entityStatesRef.current[actualEntityType];
          const currentEntityData =
            existingEntity?.id === actualEntityId ? existingEntity?.data : null;

          const sanitizedClientConfig = await sanitizeLoginContext();
          const result = await handleEntityLoad(
            actualEntityType,
            actualEntityId,
            currentEntityData as
              | Sale
              | ListingDetails
              | PurchaseOrderDetails
              | null,
            dataToGet,
            sanitizedClientConfig
          );

          // null item return means it was not found (204 status)
          if (result == null) {
            setErrorInfo({
              errorHeader: <Content id={ContentId.Error_NotFound_Header} />,
              errorMessage: (
                <Content id={ContentId.Error_NotFound_GenericMessage} />
              ),
            });
          }

          const catalogEvent = result.posEntity
            ? await getAndUpdateCatalogDataForEntity(
                result.posEntity,
                sectional || false,
                actualEntityType, // Pass the actual entity type,
                sanitizedClientConfig
              )
            : null;

          const entityState = {
            id: result.posEntityId || actualEntityId,
            displayId: result.posEntityDisplayId || null,
            type: actualEntityType,
            data: result.posEntity || null,
            event: catalogEvent?.event || result.event || null,
            loadedAt: Date.now(),
            isLoading: false,
            error: undefined,
          };

          // Update entity state in the map
          setEntityStates((prev) => ({
            ...prev,
            [actualEntityType]: entityState,
          }));

          if (entityState.data && shouldLogData) {
            addStopGapRef.current(
              logKey,
              getPosEntityStopwatchLoggingId(actualEntityType),
              getStopwatchGapsForActivePosEntityPageLoad(actualEntityType),
              StopwatchLoggingEventName.EntityLoad,
              actualEntityType
            );
          }

          if (entityState.data) {
            onSetActiveItemCompleteCallback?.(
              entityState.data as TDataItem,
              actualForceRefresh
            );
          }
        },
        (error) => {
          setActiveActionOutboxEntityType(null);

          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError('MultiEntityContextProvider.setActivePosEntity', error, {
            posEntityId: actualEntityId,
            entityType: actualEntityType,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      isLoading,
      doNotLogPageLoad,
      entityType,
      sanitizeLoginContext,
      handleEntityLoad,
      getAndUpdateCatalogDataForEntity,
      onSetActiveItemCompleteCallback,
      trackError,
    ]
  );

  const contextValue = useMemo(
    () => ({
      entityType,
      isLoading: activeEntityState?.isLoading || isLoading,
      errorInfo,
      setActivePosEntity,
      updateActivePosEntityInfo,

      // Direct entity access methods - bypass activeEntityKey entirely
      getSaleEntity: () =>
        (entityStates.Sale?.data as SaleDetails | null) || null,
      getListingEntity: () =>
        (entityStates.Listing?.data as ListingDetails | null) || null,
      getPurchaseEntity: () =>
        (entityStates.Purchase?.data as PurchaseOrderDetails | null) || null,

      // Backwards compatible properties - point to current active entity
      posEntityId: activeEntityState?.id || null,
      posEntity: (activeEntityState?.data as TDataItem) || null,
      event: activeEntityState?.event || null,
      posEntityDisplayId: activeEntityState?.displayId || null,
    }),
    [
      activeEntityState?.data,
      activeEntityState?.displayId,
      activeEntityState?.event,
      activeEntityState?.id,
      activeEntityState?.isLoading,
      entityStates.Listing?.data,
      entityStates.Purchase?.data,
      entityStates.Sale?.data,
      entityType,
      errorInfo,
      isLoading,
      setActivePosEntity,
      updateActivePosEntityInfo,
    ]
  );

  const MultiEntityContextWithSelector =
    createActivePosEntityContextWithSelector();
  return (
    <MultiEntityContextWithSelector.Provider value={contextValue}>
      {children}
    </MultiEntityContextWithSelector.Provider>
  );
};

export const ActivePosEntityContextV2Provider = memo(
  ActivePosEntityContextV2ProviderRaw
) as typeof ActivePosEntityContextV2ProviderRaw;
